import { useState } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { supabase } from '../../lib/supabase';
import { MACHINA_VERSION } from '@machina/core';

interface SignupPageProps {
  onSwitchToLogin: () => void;
}

export function SignupPage({ onSwitchToLogin }: SignupPageProps) {
  const [betaKey, setBetaKey] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [validatingKey, setValidatingKey] = useState(false);

  const { signUp, signInWithGoogle, isLoading, error, clearError } = useAuthStore();

  /**
   * Validate that the beta key exists, is active, and unclaimed.
   * The beta-keys table is locked under deny-by-default RLS, so this goes
   * through the anon-callable SECURITY DEFINER RPC (read-only validate — does
   * not burn the key, so an abandoned signup never consumes a slot).
   */
  async function validateBetaKey(key: string): Promise<boolean> {
    const { data } = await supabase.rpc('machina_validate_beta_key', {
      p_code: key.trim().toUpperCase(),
    });
    return data === true;
  }

  /**
   * Atomically claim a beta key for the signed-in user. The RPC derives the
   * claiming uid from auth.uid() server-side (no user_id arg), so this must be
   * called AFTER authentication.
   */
  async function claimBetaKey(key: string): Promise<boolean> {
    const { data } = await supabase.rpc('machina_claim_beta_key', {
      p_code: key.trim().toUpperCase(),
    });
    return data === true;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setLocalError(null);
    setSuccessMessage(null);

    if (!betaKey.trim()) {
      setLocalError('A beta key is required to create an account');
      return;
    }

    if (!email || !password || !confirmPassword) {
      setLocalError('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setLocalError('Password must be at least 6 characters');
      return;
    }

    // Validate beta key first
    setValidatingKey(true);
    const keyValid = await validateBetaKey(betaKey);
    setValidatingKey(false);

    if (!keyValid) {
      setLocalError('Invalid or already-claimed beta key');
      return;
    }

    // Sign up
    const result = await signUp(email, password, displayName || undefined);

    if (result.error) {
      if (result.error.includes('check your email')) {
        setSuccessMessage(result.error);
      }
      return;
    }

    // Claim the beta key for the new user (uid comes from auth.uid() server-side)
    const user = useAuthStore.getState().user;
    if (user) {
      const claimed = await claimBetaKey(betaKey);
      if (!claimed) {
        console.warn('[Signup] Beta key claim failed (race condition?) — admin can handle');
      }
    }
  };

  const handleGoogleSignIn = async () => {
    clearError();
    setLocalError(null);

    if (!betaKey.trim()) {
      setLocalError('Enter your beta key before signing up with Google');
      return;
    }

    // Validate beta key before redirecting
    setValidatingKey(true);
    const keyValid = await validateBetaKey(betaKey);
    setValidatingKey(false);

    if (!keyValid) {
      setLocalError('Invalid or already-claimed beta key');
      return;
    }

    // Store key in sessionStorage for claim after OAuth redirect
    sessionStorage.setItem('machina:pending-beta-key', betaKey.trim().toUpperCase());

    await signInWithGoogle();
  };

  const displayError = localError || error;

  return (
    <div style={styles.container}>
      {/* Background pattern */}
      <div style={styles.backgroundPattern} />

      {/* Signup card */}
      <div style={styles.card}>
        {/* Header ornament */}
        <div style={styles.ornamentTop}>
          <span style={styles.ornamentSymbol}>&#9876;</span>
        </div>

        {/* Title */}
        <h1 style={styles.title}>JOIN THE REALM</h1>
        <p style={styles.subtitle}>Closed Beta — Invitation Required</p>

        {/* Divider */}
        <div style={styles.divider}>
          <span style={styles.dividerDot}>&#8226;</span>
          <span style={styles.dividerLine} />
          <span style={styles.dividerDot}>&#8226;</span>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={styles.form}>
          {displayError && !successMessage && (
            <div style={styles.error}>
              {displayError}
            </div>
          )}

          {successMessage && (
            <div style={styles.success}>
              {successMessage}
            </div>
          )}

          <div style={styles.inputGroup}>
            <label style={styles.labelAccent}>Beta Key *</label>
            <input
              type="text"
              value={betaKey}
              onChange={(e) => setBetaKey(e.target.value.toUpperCase())}
              placeholder="MACHINA-ALPHA-XXX"
              style={styles.inputAccent}
              autoComplete="off"
              required
              disabled={isLoading || validatingKey}
            />
            <span style={styles.hint}>Your invitation code</span>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Display Name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your adventurer name"
              style={styles.input}
              autoComplete="name"
              disabled={isLoading}
            />
            <span style={styles.hint}>How others will see you</span>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Email *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="adventurer@realm.com"
              style={styles.input}
              autoComplete="email"
              required
              disabled={isLoading}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Password *</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              style={styles.input}
              autoComplete="new-password"
              required
              disabled={isLoading}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Confirm Password *</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repeat your password"
              style={styles.input}
              autoComplete="new-password"
              required
              disabled={isLoading}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || validatingKey || !betaKey || !email || !password || !confirmPassword}
            style={{
              ...styles.button,
              opacity: isLoading || validatingKey || !betaKey || !email || !password || !confirmPassword ? 0.6 : 1,
              cursor: isLoading || validatingKey || !betaKey || !email || !password || !confirmPassword ? 'not-allowed' : 'pointer',
            }}
          >
            {validatingKey ? 'Validating Key...' : isLoading ? 'Creating Account...' : 'Begin Your Journey'}
          </button>

          {/* OAuth divider */}
          <div style={styles.oauthDivider}>
            <span style={styles.dividerLine} />
            <span style={styles.oauthDividerText}>or</span>
            <span style={styles.dividerLine} />
          </div>

          {/* Google sign-in */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={isLoading || validatingKey}
            style={{
              ...styles.googleButton,
              opacity: isLoading || validatingKey ? 0.6 : 1,
              cursor: isLoading || validatingKey ? 'not-allowed' : 'pointer',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 18 18" style={{ marginRight: 8, opacity: 0.8 }}>
              <path fill="currentColor" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
              <path fill="currentColor" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
              <path fill="currentColor" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z"/>
              <path fill="currentColor" d="M8.98 3.58c1.16 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.9z"/>
            </svg>
            Sign up via Google
          </button>
        </form>

        {/* Bottom divider */}
        <div style={{ ...styles.divider, marginTop: 'var(--spacing-lg)' }}>
          <span style={styles.dividerLine} />
        </div>

        {/* Login prompt */}
        <p style={styles.loginPrompt}>
          Already have an account?{' '}
          <button
            onClick={onSwitchToLogin}
            style={styles.loginLink}
            type="button"
          >
            Sign in
          </button>
        </p>

        {/* Footer ornament */}
        <div style={styles.ornamentBottom}>
          <span style={styles.ornamentSymbol}>&#9830;</span>
        </div>
      </div>

      {/* Version */}
      <p style={styles.version}>v{MACHINA_VERSION}</p>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--color-background)',
    padding: 'var(--spacing-lg)',
    position: 'relative',
    overflow: 'hidden',
  },

  backgroundPattern: {
    position: 'absolute',
    inset: 0,
    opacity: 0.03,
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4af37' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
  },

  card: {
    width: '100%',
    maxWidth: '400px',
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '2px solid var(--color-accent)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 60px rgba(212, 175, 55, 0.1)',
    padding: 'var(--spacing-xl)',
    position: 'relative',
  },

  ornamentTop: {
    position: 'absolute',
    top: '-12px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'var(--color-surface)',
    padding: '0 var(--spacing-sm)',
  },

  ornamentBottom: {
    position: 'absolute',
    bottom: '-10px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'var(--color-surface)',
    padding: '0 var(--spacing-sm)',
  },

  ornamentSymbol: {
    color: 'var(--color-accent)',
    fontSize: '20px',
  },

  title: {
    textAlign: 'center',
    fontSize: '1.8rem',
    fontWeight: 700,
    letterSpacing: '0.2em',
    color: 'var(--color-accent)',
    margin: 0,
    marginTop: 'var(--spacing-sm)',
    textShadow: '0 2px 10px rgba(212, 175, 55, 0.3)',
  },

  subtitle: {
    textAlign: 'center',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    margin: 0,
    marginTop: 'var(--spacing-xs)',
    letterSpacing: '0.05em',
  },

  divider: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--spacing-sm)',
    margin: 'var(--spacing-md) 0',
  },

  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: 'var(--color-border)',
  },

  dividerDot: {
    color: 'var(--color-accent)',
    fontSize: '8px',
  },

  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-md)',
  },

  error: {
    padding: 'var(--spacing-sm) var(--spacing-md)',
    backgroundColor: 'rgba(220, 53, 69, 0.15)',
    border: '1px solid var(--color-error)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-error)',
    fontSize: 'var(--font-size-sm)',
    textAlign: 'center',
  },

  success: {
    padding: 'var(--spacing-sm) var(--spacing-md)',
    backgroundColor: 'rgba(40, 167, 69, 0.15)',
    border: '1px solid #28a745',
    borderRadius: 'var(--radius-sm)',
    color: '#28a745',
    fontSize: 'var(--font-size-sm)',
    textAlign: 'center',
  },

  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-xs)',
  },

  label: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    color: 'var(--color-text-secondary)',
    letterSpacing: '0.05em',
  },

  labelAccent: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    color: 'var(--color-accent)',
    letterSpacing: '0.05em',
  },

  input: {
    padding: 'var(--spacing-sm) var(--spacing-md)',
    backgroundColor: 'var(--color-background)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-text)',
    fontSize: 'var(--font-size-md)',
    outline: 'none',
    transition: 'border-color var(--transition-fast), box-shadow var(--transition-fast)',
  },

  inputAccent: {
    padding: 'var(--spacing-sm) var(--spacing-md)',
    backgroundColor: 'var(--color-background)',
    border: '2px solid var(--color-accent)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-accent)',
    fontSize: 'var(--font-size-md)',
    fontWeight: 600,
    fontFamily: 'monospace',
    letterSpacing: '0.1em',
    outline: 'none',
    transition: 'border-color var(--transition-fast), box-shadow var(--transition-fast)',
  },

  hint: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    fontStyle: 'italic',
  },

  button: {
    marginTop: 'var(--spacing-sm)',
    padding: 'var(--spacing-md)',
    background: 'linear-gradient(180deg, rgba(212, 175, 55, 0.3) 0%, rgba(160, 128, 48, 0.4) 100%)',
    border: '2px solid var(--color-accent)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-accent)',
    fontSize: 'var(--font-size-md)',
    fontWeight: 600,
    letterSpacing: '0.1em',
    transition: 'all var(--transition-fast)',
  },

  oauthDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
    marginTop: 'var(--spacing-sm)',
  },

  oauthDividerText: {
    color: 'var(--color-text-secondary)',
    fontSize: 'var(--font-size-sm)',
    textTransform: 'lowercase',
  },

  googleButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--spacing-sm) var(--spacing-md)',
    backgroundColor: 'transparent',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-text-secondary)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    transition: 'all var(--transition-fast)',
    letterSpacing: '0.05em',
  },

  loginPrompt: {
    textAlign: 'center',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    margin: 0,
  },

  loginLink: {
    background: 'none',
    border: 'none',
    color: 'var(--color-accent)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'none',
  },

  version: {
    position: 'absolute',
    bottom: 'var(--spacing-md)',
    color: 'var(--color-text-muted)',
    fontSize: 'var(--font-size-xs)',
    margin: 0,
  },
};
